import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  ArrowRight, Tv, Smartphone, Radio, Cloud, Shield, BarChart3, CircleDollarSign,
  Sparkles, Play, Tv2, MonitorPlay, Smartphone as SmartphoneIcon, Globe2, Cast,
  Server, Layers, HardDrive, Cog,
} from "lucide-react";
import { useI18n, pickLang } from "@/lib/I18nProvider";
import { HERO, STATS, PLATFORMS, SERVICES, SECTIONS, PARTNERS } from "@/lib/i18n";

const PlatformIcons = {
  "Roku": MonitorPlay,
  "Apple TV": Tv2,
  "Fire TV": Tv2,
  "Android TV": Tv2,
  "Samsung Tizen": MonitorPlay,
  "LG webOS": MonitorPlay,
  "iOS": SmartphoneIcon,
  "Android": SmartphoneIcon,
  "Web TV": Globe2,
  "Chromecast": Cast,
  "AirPlay": Cast,
  "Set-Top Box": HardDrive,
};

const ServiceIcons = {
  "core-cms": Server,
  "front-apps": Layers,
  "live-streaming": Radio,
  "cloud-playout": Cloud,
  "transcoder": Sparkles,
  "dvr-catchup": Play,
  "secure": Shield,
  "monetization": CircleDollarSign,
  "stb": HardDrive,
  "casting": Cast,
  "analytics": BarChart3,
  "ingest": Cog,
};

export default function Home() {
  const { lang, t } = useI18n();
  const hero = pickLang(HERO, lang);
  const stats = pickLang(STATS, lang);
  const services = pickLang(SERVICES, lang);
  const sections = pickLang(SECTIONS, lang);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-radial-glow">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="absolute inset-0 scanline pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-28 md:pt-32 md:pb-40">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="overline">
            {hero.overline}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }}
            className="font-display mt-6 text-[2.5rem] sm:text-5xl md:text-7xl lg:text-[5.5rem] font-bold leading-[1.02] md:leading-[0.95] tracking-tighter max-w-5xl"
          >
            {hero.title_a} <span className="gradient-text">{hero.title_b}</span>
          </motion.h1>

          {/* Streaming logos — right after the headline */}
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.12 }}
            className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4"
          >
            <span className="font-mono text-[10px] md:text-[11px] uppercase tracking-[0.22em] text-slate-300 whitespace-nowrap">
              {t("common.right_next_to")}
            </span>
            <div className="flex flex-wrap items-center gap-3 md:gap-4" data-testid="hero-streaming-logos">
              <div data-testid="streaming-logo-netflix" className="blink-soft h-14 md:h-16 w-28 md:w-32 px-3 rounded-xl bg-white flex items-center justify-center shadow-[0_8px_24px_-8px_rgba(0,0,0,0.5)]" style={{ animationDelay: "0s" }}>
                <img src="https://customer-assets.emergentagent.com/job_push2stream-redesign/artifacts/gs4fzyqq_Netflix-logo.png" alt="Netflix" className="max-h-9 md:max-h-10 max-w-full object-contain" />
              </div>
              <div data-testid="streaming-logo-hulu" className="blink-soft h-14 md:h-16 w-28 md:w-32 px-3 rounded-xl bg-black flex items-center justify-center shadow-[0_8px_24px_-8px_rgba(0,0,0,0.5)]" style={{ animationDelay: "0.5s" }}>
                <img src="https://customer-assets.emergentagent.com/job_push2stream-redesign/artifacts/3pgzpl4s_Hulu_logo_%282018%29.svg.png" alt="Hulu" className="max-h-10 md:max-h-12 max-w-full object-contain" />
              </div>
              <div data-testid="streaming-logo-starz" className="blink-soft h-14 md:h-16 w-28 md:w-32 px-3 rounded-xl bg-white flex items-center justify-center shadow-[0_8px_24px_-8px_rgba(0,0,0,0.5)]" style={{ animationDelay: "1s" }}>
                <img src="https://customer-assets.emergentagent.com/job_push2stream-redesign/artifacts/we0xx5x2_STARZ.jpg.jpeg" alt="STARZ" className="max-h-10 md:max-h-12 max-w-full object-contain" />
              </div>
              <div data-testid="streaming-logo-paramount" className="blink-soft h-14 md:h-16 w-28 md:w-32 px-2 rounded-xl bg-white flex items-center justify-center shadow-[0_8px_24px_-8px_rgba(0,0,0,0.5)]" style={{ animationDelay: "1.5s" }}>
                <img src="https://customer-assets.emergentagent.com/job_push2stream-redesign/artifacts/g7m6p4wu_paramount-logo-png_seeklogo-397501.png" alt="Paramount+" className="max-h-12 md:max-h-14 max-w-full object-contain" />
              </div>
            </div>
          </motion.div>

          {/* Trademark disclaimer */}
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-3 text-[11px] text-slate-400 max-w-xl leading-relaxed"
            data-testid="trademark-disclaimer"
          >
            {t("common.trademark_disclaimer")}
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.22 }}
            className="mt-8 text-base md:text-xl text-slate-200 max-w-2xl leading-relaxed"
          >
            {hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.32 }}
            className="mt-10 flex flex-wrap items-center gap-3"
          >
            <Link to="/demo" data-testid="hero-cta-demo" className="btn-primary">
              {t("cta.demo")} <ArrowRight size={18} />
            </Link>
            <Link to="/services" data-testid="hero-cta-services" className="btn-ghost">
              {t("cta.see_services")}
            </Link>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-6"
          >
            {stats.map((s, i) => (
              <div key={i} className="glass rounded-2xl p-6" data-testid={`stat-${i}`}>
                <div className="font-display text-3xl md:text-4xl font-bold gradient-text">{s.value}</div>
                <div className="mt-2 text-xs font-mono uppercase tracking-widest text-slate-400">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* PLATFORM MARQUEE */}
      <section className="py-16 border-y border-white/10 bg-[#324567]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="overline">{sections.platforms_overline}</div>
            <h2 className="font-display mt-4 text-3xl md:text-4xl font-bold tracking-tight text-white">
              {sections.platforms_title_a} <span className="gradient-text">{sections.platforms_title_b}</span>
            </h2>
          </div>
          <p className="text-slate-400 max-w-md">{sections.platforms_subtitle}</p>
        </div>
        <div className="marquee-fade">
          <Marquee gradient={false} speed={40} pauseOnHover>
            {PLATFORMS.concat(PLATFORMS).map((p, i) => {
              const Icon = PlatformIcons[p.name] || Tv;
              return (
                <div key={i} className="flex items-center gap-3 mx-10 text-slate-400 hover:text-white transition-colors">
                  <Icon size={32} />
                  <span className="font-display text-2xl md:text-3xl font-semibold">{p.name}</span>
                </div>
              );
            })}
          </Marquee>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 items-end mb-14">
            <div className="md:col-span-7">
              <div className="overline">{sections.services_overline}</div>
              <h2 className="font-display mt-4 text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05] text-white">
                {sections.services_title_a}<span className="gradient-text">{sections.services_title_b}</span>
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-lg text-slate-300 leading-relaxed">{sections.services_subtitle}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) => {
              const Icon = ServiceIcons[s.slug] || Sparkles;
              return (
                <motion.div
                  key={s.slug}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.5, delay: i * 0.03 }}
                  data-testid={`service-card-${s.slug}`}
                  className="card-hover glass rounded-2xl p-7 flex flex-col"
                >
                  <div className="w-12 h-12 rounded-xl gradient-bg flex items-center justify-center text-black">
                    <Icon size={22} strokeWidth={2.2} />
                  </div>
                  <h3 className="font-display mt-5 text-xl font-bold tracking-tight text-white">{s.title}</h3>
                  <p className="mt-3 text-slate-300 leading-relaxed text-sm">{s.summary}</p>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-12 flex justify-center">
            <Link to="/services" data-testid="home-services-cta" className="btn-ghost">
              {t("cta.explore_services")} <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* MOXYPLAY FEATURE */}
      <section className="py-24 md:py-32 border-t border-white/10 bg-[#324567]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <div className="overline">{sections.moxy_overline}</div>
            <h2 className="font-display mt-4 text-4xl md:text-5xl font-bold tracking-tighter leading-[1.05] text-white">
              {sections.moxy_title_a}<span className="gradient-text">{sections.moxy_title_b}</span>
            </h2>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed">{sections.moxy_subtitle}</p>

            <Link to="/services" data-testid="moxy-cta" className="btn-primary mt-10">
              {t("cta.see_services")} <ArrowRight size={18} />
            </Link>
          </div>

          <div className="md:col-span-6">
            <div className="relative">
              <div className="absolute -inset-4 rounded-3xl gradient-bg opacity-25 blur-3xl" />
              <div className="relative glass rounded-3xl p-2 overflow-hidden">
                <div className="rounded-2xl overflow-hidden bg-[#0E1421] aspect-video relative">
                  <div className="absolute inset-0 bg-grid opacity-40" />
                  <div className="absolute top-4 left-4 flex items-center gap-2 z-10">
                    <span className="w-2 h-2 rounded-full bg-red-500 pulse-dot" />
                    <span className="text-[10px] font-mono uppercase tracking-widest text-white/90">LIVE · 4K · 1.2M VIEWERS</span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 z-10">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-2">NOW PLAYING</div>
                    <div className="font-display text-2xl font-bold text-white">Primetime · Channel 01</div>
                    <div className="mt-3 h-1 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full w-2/3 gradient-bg" />
                    </div>
                  </div>
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full gradient-bg flex items-center justify-center text-black float">
                    <Play size={28} fill="currentColor" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PARTNERS */}
      <section className="py-20 border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 mb-10 text-center">
          <div className="overline justify-center inline-flex">{sections.partners_overline}</div>
          <h2 className="font-display mt-4 text-2xl md:text-3xl font-bold tracking-tight text-slate-300">
            {sections.partners_title}
          </h2>
        </div>
        <div className="marquee-fade">
          <Marquee gradient={false} speed={30} pauseOnHover>
            {PARTNERS.concat(PARTNERS).map((p, i) => (
              <div key={i} className="mx-12 font-display text-2xl md:text-3xl text-slate-500 hover:text-white transition-colors tracking-tight">
                {p}
              </div>
            ))}
          </Marquee>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="relative rounded-3xl overflow-hidden gradient-bg p-12 md:p-20">
            <div className="absolute inset-0 bg-grid opacity-15" />
            <div className="relative grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-8">
                <div className="font-mono text-xs uppercase tracking-[0.22em] text-black/70">{sections.closing_overline}</div>
                <h2 className="font-display mt-4 text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05] text-black">
                  {sections.closing_title_a} <br className="hidden md:block" /> {sections.closing_title_b}
                </h2>
                <p className="mt-6 text-black/80 text-lg max-w-2xl">{sections.closing_subtitle}</p>
              </div>
              <div className="md:col-span-4 flex md:justify-end">
                <Link to="/demo" data-testid="closing-cta-demo" className="bg-[#0F172A] text-white font-bold rounded-full px-8 py-4 inline-flex items-center gap-2 hover:bg-[#1E293B] transition-colors">
                  {t("cta.book_demo")} <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
